import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiChevronLeft,
  FiChevronRight,
  FiMoon,
  FiRefreshCw,
  FiSearch,
  FiSun,
  FiX,
} from 'react-icons/fi';
import { useNdTheme } from '../utils/ndTheme';
import { getApiBaseURL } from '../utils/apiUrl';
import { toCyrillicFio } from '../utils/latinToCyrillicFio';
import {
  buildAppUsersAvatarLookupMap,
  avatarSrcForImg,
  resolveAvatarFromUserMap,
} from '../utils/reportAvatars';
import '../styles/newDesign.css';

const AVATAR_COLORS = ['#f97316', '#2dd4bf', '#38bdf8', '#a78bfa', '#fbbf24', '#34d399', '#f472b6', '#f0566a'];

const api = (path) => `${getApiBaseURL()}${path.startsWith('/') ? '' : '/'}${path}`;

function authHeaders() {
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function toLocalDateStr(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function todayLocal() {
  return toLocalDateStr(new Date());
}

function formatYmd(ymd) {
  if (!ymd) return '';
  const [y, m, d] = String(ymd).split('-');
  return `${d}.${m}.${y}`;
}

function formatClock(dtStr) {
  if (!dtStr) return '—';
  const d = new Date(dtStr);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
}

function formatDurationSec(sec) {
  const n = Number(sec) || 0;
  if (n <= 0) return '0с';
  if (n < 60) return `${n}с`;
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const s = Math.floor(n % 60);
  if (h > 0) return `${h}ч ${m}м`;
  return `${m}м${s > 0 ? ` ${s}с` : ''}`;
}

function ndInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const letters = parts.length > 1 ? parts[0][0] + parts[1][0] : parts[0].slice(0, 2);
  return letters.toUpperCase();
}

function ndColorOf(name) {
  const s = String(name || '?');
  let sum = 0;
  for (let i = 0; i < s.length; i += 1) sum += s.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function displayName(row) {
  return toCyrillicFio(row?.user_username || row?.username || row?.fio || '') || 'Без имени';
}

export default function AppUsageBoard({ onClose, isMobile = false }) {
  const { isLight: ndIsLight, toggle: toggleNdTheme } = useNdTheme();
  const [date, setDate] = useState(todayLocal);
  const [report, setReport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const [includeAll, setIncludeAll] = useState(true);
  const [detailsRow, setDetailsRow] = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState(1);
  const [spin, setSpin] = useState(false);
  const [appUsers, setAppUsers] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAppUsers([]);
      return;
    }
    fetch(api('/api/users'), { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const arr = Array.isArray(d) ? d : (d?.users || []);
        setAppUsers(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setAppUsers([]));
  }, []);

  const avatarMap = useMemo(() => buildAppUsersAvatarLookupMap(appUsers), [appUsers]);

  const fetchReport = useCallback(async () => {
    if (!date) return;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ start: date, end: date, includeAll: String(includeAll) });
      const res = await fetch(api(`/api/app-usage/report?${params.toString()}`), { headers: authHeaders() });
      const data = await res.json();
      setReport(Array.isArray(data.report) ? data.report : []);
    } catch (e) {
      setReport([]);
      setError(e.message || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  }, [date, includeAll]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const shiftDay = (delta) => {
    const [y, m, d] = date.split('-').map(Number);
    const next = new Date(y, m - 1, d);
    next.setDate(next.getDate() + delta);
    setDate(toLocalDateStr(next));
  };

  const handleRefresh = async () => {
    setSpin(true);
    await fetchReport();
    setTimeout(() => setSpin(false), 600);
  };

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => -d);
    else {
      setSortKey(key);
      setSortDir(1);
    }
  };

  const filtered = useMemo(() => {
    const search = q.trim().toLowerCase();
    let list = report.filter((row) => {
      if (!includeAll && !row.first_start) return false;
      if (!search) return true;
      const name = displayName(row).toLowerCase();
      const username = String(row.user_username || '').toLowerCase();
      return name.includes(search) || username.includes(search);
    });
    list = [...list].sort((a, b) => {
      if (sortKey === 'dur') return ((Number(a.span_seconds) || 0) - (Number(b.span_seconds) || 0)) * sortDir;
      return displayName(a).localeCompare(displayName(b), 'ru', { sensitivity: 'base' }) * sortDir;
    });
    return list;
  }, [report, q, includeAll, sortKey, sortDir]);

  const stats = useMemo(() => {
    const launched = report.filter((r) => r.first_start).length;
    const hours = Math.round(report.reduce((sum, r) => sum + (Number(r.span_seconds) || 0), 0) / 3600);
    return { launched, hours, people: report.length };
  }, [report]);

  const maxSpan = useMemo(() => {
    const max = filtered.reduce((m, r) => Math.max(m, Number(r.span_seconds) || 0), 0);
    return max > 0 ? max : 1;
  }, [filtered]);

  const avatarFor = (row) => {
    const raw = (row.avatar && String(row.avatar).trim()) || resolveAvatarFromUserMap({
      username: row.user_username,
      fio: displayName(row),
    }, avatarMap);
    return raw ? avatarSrcForImg(raw) : null;
  };

  const detailsSessions = useMemo(() => {
    if (!detailsRow || !Array.isArray(detailsRow.sessions)) return [];
    return detailsRow.sessions.slice().sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  }, [detailsRow]);

  const renderRowBody = (row, idx) => {
    const name = displayName(row);
    const src = avatarFor(row);
    const color = ndColorOf(name);
    const hasStart = Boolean(row.first_start);
    const pct = Math.round(((Number(row.span_seconds) || 0) / maxSpan) * 100);
    return { name, src, color, hasStart, pct, idx };
  };

  return (
    <div
      className={`nd-runWrap${isMobile ? ' nd-mobile' : ''}`}
      onClick={isMobile ? undefined : onClose}
    >
      <div className="nd-runSheet" onClick={(e) => e.stopPropagation()}>
        <div className="nd-sheetHead">
          <div className="nd-sheetIco">🚀</div>
          <h2>
            Отчет запусков приложения
            <span>кто и когда запускал приложение · первый запуск · последнее закрытие</span>
          </h2>
          <button
            type="button"
            className="nd-themeBtn"
            onClick={toggleNdTheme}
            title={ndIsLight ? 'Включить тёмную тему' : 'Включить светлую тему'}
          >
            {ndIsLight ? <FiMoon /> : <FiSun />}
          </button>
          {onClose && (
            <button type="button" className="nd-sheetClose" onClick={onClose} aria-label="Закрыть" title="Закрыть">
              <FiX />
            </button>
          )}
        </div>

        <div className="nd-statChips">
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-green)' }}><i />Запускали <b>{stats.launched}</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-orange)' }}><i />Суммарно <b>{stats.hours} ч</b></span>
          <span className="nd-sChip" style={{ '--sc': 'var(--nd-blue)' }}><i />Всего сотрудников <b>{stats.people}</b></span>
        </div>

        <div>
          <span className="nd-lbl">Быстрая навигация по дням</span>
          <div className="nd-dayNav">
            <button type="button" className="nd-dNavBtn" onClick={() => shiftDay(-1)} aria-label="Предыдущий день">
              <FiChevronLeft />
            </button>
            <input className="nd-dateLbl" type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
            <button type="button" className="nd-dNavBtn" onClick={() => shiftDay(1)} aria-label="Следующий день">
              <FiChevronRight />
            </button>
          </div>
        </div>

        <div className="nd-runCtrl nd-glass">
          <span className="nd-lb">Дата:</span>
          <input type="date" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} />
          <button
            type="button"
            className={`nd-runTgl${includeAll ? ' nd-on' : ''}`}
            onClick={() => setIncludeAll((v) => !v)}
          >
            <span className="nd-sw" />
            <span>Показать всех</span>
          </button>
          <div className="nd-runSearch">
            <span className="nd-sIco"><FiSearch size={14} /></span>
            <input
              type="text"
              placeholder="Поиск по ФИО..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>
          <button type="button" className={`nd-runRef${spin ? ' nd-spin' : ''}`} onClick={handleRefresh} disabled={loading}>
            <FiRefreshCw className="nd-sp" />
            {loading ? 'Загрузка...' : 'Обновить'}
          </button>
        </div>

        {error && <div className="nd-runEmpty">{error}</div>}

        <div className="nd-runTblWrap nd-glass">
          <table className="nd-runTbl">
            <thead>
              <tr>
                <th className={`nd-sort${sortKey === 'name' ? ' nd-sorted' : ''}`} onClick={() => toggleSort('name')}>Имя ↕</th>
                <th>Дата</th>
                <th>Первый запуск</th>
                <th>Последнее закрытие</th>
                <th className={`nd-sort${sortKey === 'dur' ? ' nd-sorted' : ''}`} onClick={() => toggleSort('dur')}>Промежуток ↕</th>
                <th>Детали</th>
              </tr>
            </thead>
            <tbody>
              {loading && filtered.length === 0 ? (
                <tr><td colSpan={6} className="nd-runEmpty">Загрузка...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={6} className="nd-runEmpty">Никого не найдено</td></tr>
              ) : filtered.map((row, idx) => {
                const { name, src, color, hasStart, pct } = renderRowBody(row, idx);
                return (
                  <tr key={`${row.user_username}-${row.date}-${idx}`} className={hasStart ? '' : 'nd-zero'} style={{ animationDelay: `${Math.min(idx * 35, 420)}ms` }}>
                    <td>
                      <div className="nd-runWho">
                        <span className="nd-runAv" style={{ '--cc': color }}>
                          {src ? <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : ndInitials(name)}
                        </span>
                        {name}
                      </div>
                    </td>
                    <td className="nd-runTc">{row.date || date}</td>
                    <td className={`nd-runTc${hasStart ? ' nd-has' : ''}`}>{formatClock(row.first_start)}</td>
                    <td className={`nd-runTc${row.last_stop ? ' nd-has' : ''}`}>{formatClock(row.last_stop)}</td>
                    <td>
                      <div className="nd-runDur">
                        <b>{formatDurationSec(row.span_seconds)}</b>
                        <span className="nd-runBar"><i style={{ width: `${pct}%` }} /></span>
                      </div>
                    </td>
                    <td>
                      <button type="button" className="nd-runMore" onClick={() => setDetailsRow(row)}>Подробнее</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="nd-runCards">
          {loading && filtered.length === 0 && <div className="nd-runEmpty">Загрузка...</div>}
          {!loading && filtered.length === 0 && <div className="nd-runEmpty">Никого не найдено</div>}
          {filtered.map((row, idx) => {
            const { name, src, color, hasStart, pct } = renderRowBody(row, idx);
            return (
              <div key={`${row.user_username}-card-${idx}`} className={`nd-runCard nd-glass${hasStart ? '' : ' nd-zero'}`}>
                <div className="nd-runWho">
                  <span className="nd-runAv" style={{ '--cc': color }}>
                    {src ? <img src={src} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} /> : ndInitials(name)}
                  </span>
                  {name}
                </div>
                <div className="nd-runCardMeta">
                  <span>Дата: <b>{row.date || date}</b></span>
                  <span>Промежуток: <b>{formatDurationSec(row.span_seconds)}</b></span>
                  <span>Первый запуск: <b>{formatClock(row.first_start)}</b></span>
                  <span>Закрытие: <b>{formatClock(row.last_stop)}</b></span>
                </div>
                <span className="nd-runBar" style={{ width: '100%' }}><i style={{ width: `${pct}%` }} /></span>
                <button type="button" className="nd-runMore" onClick={() => setDetailsRow(row)}>Подробнее</button>
              </div>
            );
          })}
        </div>

        <div className="nd-footRow">
          {onClose && (
            <button type="button" className="nd-closeBig" onClick={onClose}>✕ Закрыть</button>
          )}
          <span className="nd-footHint">Клик по «Имя» или «Промежуток» — сортировка</span>
        </div>
      </div>

      {detailsRow && (
        <div
          className="nd-subModal"
          onClick={(e) => {
            e.stopPropagation();
            setDetailsRow(null);
          }}
        >
          <div className="nd-mBox" onClick={(e) => e.stopPropagation()}>
            <h3>
              <span>🚀 {displayName(detailsRow)}</span>
              <button type="button" onClick={() => setDetailsRow(null)} aria-label="Закрыть">✕</button>
            </h3>
            <div className="nd-mSub">Сессии приложения за {formatYmd(detailsRow.date || date)}</div>
            <div className="nd-mStats">
              <div className="nd-mStat"><span>Первый запуск</span><b>{formatClock(detailsRow.first_start)}</b></div>
              <div className="nd-mStat"><span>Закрытие</span><b>{formatClock(detailsRow.last_stop)}</b></div>
              <div className="nd-mStat"><span>Промежуток</span><b>{formatDurationSec(detailsRow.span_seconds)}</b></div>
            </div>
            <div className="nd-tlList">
              {detailsSessions.length === 0 ? (
                <div className="nd-tlRow"><span className="nd-tt" style={{ color: 'var(--nd-mut)' }}>Запусков не было</span></div>
              ) : detailsSessions.map((s, i) => (
                <div key={s.id || `${s.start_at}-${i}`} className="nd-tlRow" style={{ animationDelay: `${i * 60}ms` }}>
                  <span className="nd-tm">{formatClock(s.start_at)}–{formatClock(s.end_at)}</span>
                  <span className="nd-tt">Сессия {i + 1}</span>
                  <span className="nd-val">{formatDurationSec(s.duration_sec)}</span>
                </div>
              ))}
            </div>
            <div className="nd-mActs">
              <button type="button" className="nd-btn nd-ghost" onClick={() => setDetailsRow(null)}>Закрыть</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
